import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { SamlAuthService } from './saml-auth-service';

const AuthError: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const authService = new SamlAuthService();

    const reason = searchParams.get('reason');

    const errorMessages: Record<string, { title: string; message: string; canRetry: boolean }> = {
        'not_authenticated': {
            title: 'Authentication Required',
            message: 'You need to be logged in to access this application.',
            canRetry: true
        },
        'processing_failed': {
            title: 'Authentication Error',
            message: 'There was an error processing your authentication. Please try again.',
            canRetry: true
        },
        'callback_failed': {
            title: 'Login Failed',
            message: 'The login process could not be completed. Please try again.',
            canRetry: true
        },
        'callback_exception': {
            title: 'Technical Error',
            message: 'A technical error occurred during login. Please contact support if this persists.',
            canRetry: true
        },
        'token_retrieval_failed': {
            title: 'Session Error',
            message: 'Could not retrieve your session information. Please try logging in again.',
            canRetry: true
        },
        'no_session': {
            title: 'Invalid Session',
            message: 'No valid session found. Please log in again.',
            canRetry: true
        },
        'unauthenticated': {
            title: 'Please Log In',
            message: 'You are not currently logged in to the application.',
            canRetry: true
        }
    };

    const currentError = errorMessages[reason || 'unknown'] || {
        title: 'Unknown Error',
        message: 'An unexpected error occurred. Please try logging in again.',
        canRetry: true
    };

    const handleRetryLogin = () => {
        console.log('Retrying SAML login...');
        authService.login();
    };

    const handleGoHome = () => {
        // Clear any stored auth data and redirect to home
        localStorage.removeItem('authData');
        navigate('/', { replace: true });
    };

    return (
        <div style={styles.container}>
            <div style={styles.errorBox}>
                <div style={styles.iconContainer}>
                    <span style={styles.errorIcon}></span>
                </div>

                <h1 style={styles.title}>{currentError.title}</h1>
                <p style={styles.message}>{currentError.message}</p>

                {reason && (
                    <div style={styles.detailsContainer}>
                        <details style={styles.details}>
                            <summary style={styles.detailsSummary}>Technical Details</summary>
                            <p style={styles.detailsText}>Error reason: {reason}</p>
                            <p style={styles.detailsText}>
                                If this error persists, please contact your system administrator
                                and provide this error code.
                            </p>
                        </details>
                    </div>
                )}

                <div style={styles.buttonContainer}>
                    {currentError.canRetry && (
                        <button
                            onClick={handleRetryLogin}
                            style={styles.primaryButton}
                        >
                            Try Logging In Again
                        </button>
                    )}

                    <button
                        onClick={handleGoHome}
                        style={styles.secondaryButton}
                    >
                        Go to Home Page
                    </button>
                </div>

                <div style={styles.helpText}>
                    <p>
                        Need help? Contact your system administrator or IT support.
                    </p>
                </div>
            </div>
        </div>
    );
};

// Inline styles (you can move these to CSS if preferred)
const styles = {
    container: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        padding: '20px'
    },
    errorBox: {
        backgroundColor: 'white',
        padding: '40px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        textAlign: 'center' as const,
        maxWidth: '500px',
        width: '100%'
    },
    iconContainer: {
        marginBottom: '20px'
    },
    errorIcon: {
        fontSize: '48px'
    },
    title: {
        color: '#d32f2f',
        marginBottom: '16px',
        fontSize: '24px',
        fontWeight: 'bold'
    },
    message: {
        color: '#666',
        marginBottom: '24px',
        fontSize: '16px',
        lineHeight: '1.5'
    },
    detailsContainer: {
        marginBottom: '24px',
        textAlign: 'left' as const
    },
    details: {
        backgroundColor: '#f9f9f9',
        padding: '12px',
        borderRadius: '4px',
        border: '1px solid #e0e0e0'
    },
    detailsSummary: {
        cursor: 'pointer',
        fontWeight: 'bold',
        marginBottom: '8px'
    },
    detailsText: {
        fontSize: '14px',
        color: '#666',
        margin: '4px 0'
    },
    buttonContainer: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        flexWrap: 'wrap' as const,
        marginBottom: '24px'
    },
    primaryButton: {
        backgroundColor: '#1976d2',
        color: 'white',
        border: 'none',
        padding: '12px 24px',
        borderRadius: '4px',
        fontSize: '16px',
        cursor: 'pointer',
        transition: 'background-color 0.2s'
    },
    secondaryButton: {
        backgroundColor: 'transparent',
        color: '#1976d2',
        border: '1px solid #1976d2',
        padding: '12px 24px',
        borderRadius: '4px',
        fontSize: '16px',
        cursor: 'pointer',
        transition: 'all 0.2s'
    },
    helpText: {
        fontSize: '14px',
        color: '#999',
        borderTop: '1px solid #e0e0e0',
        paddingTop: '16px'
    }
};

export default AuthError;